import React, { useState } from "react";
import {
  LayoutDynamic24Regular,
  Server24Regular,
  Shield24Regular,
  BrainSparkle24Regular,
  BuildingBank24Regular,
  ArrowRightRegular,
  CheckmarkRegular,
} from "@fluentui/react-icons";
import "./ServicesSection.scss";

interface ServiceItem {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  accent: string;
}

const SERVICES: ServiceItem[] = [
  {
    id: "custom-dev",
    number: "01",
    title: "Proprietary Custom Software",
    description:
      "High-performance enterprise architectures engineered for scale. Designed to resolve complex transaction bottlenecks while handling offline fallbacks gracefully.",
    icon: <LayoutDynamic24Regular />,
    accent: "#3b82f6",
    features: [
      "Docker & Kubernetes Orchestration",
      "Optimised DB Query Layer",
      "Enterprise API Gateways",
      "Offline-First Sync Enablers",
    ],
  },
  {
    id: "devops-cloud",
    number: "02",
    title: "DevOps & Sovereign Cloud",
    description:
      "Secure migration pathways across multi-region infrastructure. Deployed with optimal geographic latency directly inside metropolitan hubs for seamless system response.",
    icon: <Server24Regular />,
    accent: "#8b5cf6",
    features: [
      "Sub-50ms Latency Targets",
      "Multi-Region Live Replication",
      "Dynamic Load Balancing",
      "Auto-Heal Monitoring Loops",
    ],
  },
  {
    id: "sec-audit",
    number: "03",
    title: "Cybersecurity & Cryptography",
    description:
      "Protection strategies adhering strictly to NDPA guidelines — HSM security keys, secure SMTP relays, 256-bit token tunnels, and granular codebase compliance checks.",
    icon: <Shield24Regular />,
    accent: "#22c55e",
    features: [
      "Bank-Grade HSM Encryption",
      "NDPA Regulatory Alignment",
      "Penetration Vulnerability Scans",
      "Identity Access Management",
    ],
  },
  {
    id: "artificial-intelligence",
    number: "04",
    title: "Agentic AI & Hyper-Automation",
    description:
      "Autonomous LLM task runners that automate clerical jobs, parse invoice strings, evaluate claims, and trigger software APIs without routine human supervision.",
    icon: <BrainSparkle24Regular />,
    accent: "#f97316",
    features: [
      "CAEL Intelligent Decision Engine",
      "Automated Invoice OCR Parsing",
      "Human-in-the-Loop Triggers",
      "Natural Language Queries",
    ],
  },
  {
    id: "enterprise-pms",
    number: "05",
    title: "Special Enterprise Portals",
    description:
      "Turnkey administrative software across schools and health maintenance providers in West Africa — complete with billing layers and automated certificate generation.",
    icon: <BuildingBank24Regular />,
    accent: "#14b8a6",
    features: [
      "Academic Grading Modules",
      "PMS Interactive Booking Grids",
      "HMO Claims Auto-authorisation",
      "Secure E-Payment Checkouts",
    ],
  },
];

const ServicesSection: React.FC = () => {
  const [active, setActive] = useState<string>(SERVICES[0].id);
  const activeService = SERVICES.find((s) => s.id === active)!;

  return (
    <section className="sv-section">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="sv-header">
        <div className="sv-header__left">
          <span className="sv-eyebrow">Engineering Capability</span>
          <h2 className="sv-title">
            What we build,<br />
            <span className="sv-title--dim">and how we build it.</span>
          </h2>
        </div>
        <p className="sv-header__desc">
          CILPRON combines modern digital systems design with deep African market
          expertise — eliminating bottlenecks and enforcing software governance at every layer.
        </p>
      </div>

      {/* ── Split layout ────────────────────────────────────── */}
      <div className="sv-body">

        {/* Left — numbered service list */}
        <nav className="sv-list" aria-label="Services">
          {SERVICES.map((s) => (
            <button
              key={s.id}
              className={`sv-item${active === s.id ? " sv-item--active" : ""}`}
              onClick={() => setActive(s.id)}
              style={{ "--accent": s.accent } as React.CSSProperties}
              aria-pressed={active === s.id}
            >
              <span className="sv-item__num">{s.number}</span>
              <span className="sv-item__title">{s.title}</span>
              <ArrowRightRegular className="sv-item__arrow" />
            </button>
          ))}
        </nav>

        {/* Right — detail panel */}
        <div className="sv-panel" style={{ "--accent": activeService.accent } as React.CSSProperties}>

          {/* Accent glow */}
          <div className="sv-panel__glow" />

          {/* Top row */}
          <div className="sv-panel__top">
            <div className="sv-panel__icon-wrap">
              {activeService.icon}
            </div>
            <span className="sv-panel__num">{activeService.number}</span>
          </div>

          <h3 className="sv-panel__title">{activeService.title}</h3>
          <p className="sv-panel__desc">{activeService.description}</p>

          {/* Feature list */}
          <div className="sv-panel__features">
            <div className="sv-panel__features-label">Core technologies deployed</div>
            <ul className="sv-panel__feature-list">
              {activeService.features.map((f) => (
                <li key={f} className="sv-panel__feature-item">
                  <span className="sv-panel__check">
                    <CheckmarkRegular />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          {/* <a href={`/${activeService.id}`} className="sv-panel__cta">
            Learn more about this service
            <ArrowRightRegular />
          </a> */}
        </div>
      </div>

      {/* ── Stats strip ─────────────────────────────────────── */}
      
    </section>
  );
};

export default ServicesSection;